import { computed, observable } from "mobx";
import { stateController } from "../../../../cdm/controllers/state-controller";
import { UIText } from "../../../../config/lang-config";
import { clientController } from "../../../../cdm/controllers/client-controller";
import { apiController } from "../../../../cdm/controllers/api-controller";
import { apiService } from "../../../../cdm/services/api-service";
import { mcbEndpointConfig } from "../../config/api-config";
import { evalStringBoolean, isEmpty } from "../../../../utils/helpers";
import {
  groupTypeRoleIds,
  serviceRecipientGroupTypeIds
} from "../../../../config/variable-config";
import { isSnSOrAdmin } from "../../utils/helper";
import { lockTopic } from "../../../../cdm/lib/topic-utilities";
import { paidCaregiverFeatureRestricted } from "../../lib/group-utilities-mcb";
import {
  applyDisapprovalTime,
  getDisapprovalTime,
  canSubmitDisapprovalReason,
  normalizeDisapprovalReason
} from "./helpers";

export class ShiftSummaryController {
  @observable lastScreen;

  @observable buttonReady = false;
  @observable loading = true;
  @observable approveLoading = false;
  @observable disapproveLoading = false;

  @observable reportData = {};
  @observable reportHtml = "";
  @observable approvalGiven = false;
  @observable hasHourlyRate = false;
  @observable pendingDisapprovalTime = 0;
  // @observable isApproved = false;

  @computed get user() {
    return clientController.client.user || {};
  }
  @computed get isVisitor() {
    return clientController.isVisitor;
  }
  @computed get isAdmin() {
    return this.lastScreen === "AdminCentre" || isSnSOrAdmin(this.user);
  }
  @computed get groupId() {
    return Number(
      this.props.navigation.getParam("group") || stateController.viewGroupId
    );
  }
  @computed get group() {
    return clientController.findGroupById(this.groupId);
  }
  @computed get selfMember() {
    return (
      (Array.isArray(this.group.members) &&
        this.group.members.find(m => m.userId === clientController.userId)) ||
      {}
    );
  }
  @computed get myRole() {
    const roles = (Array.isArray(this.group.roles) && this.group.roles) || [];
    return (
      roles.find(r => {
        return (
          Array.isArray(r.members) &&
          r.members.find(m => m.id === this.selfMember.id)
        );
      }) || {}
    );
  }
  @computed get caregiverId() {
    return Number(this.props.navigation.getParam("caregiver"));
  }
  @computed get caregiverMember() {
    return clientController.findMemberById(this.caregiverId);
  }

  // Viewing report of myself's
  @computed get isSelf() {
    return this.caregiverId === this.selfMember.id;
  }
  @computed get startDate() {
    return Number(this.props.navigation.getParam("startDate"));
  }
  @computed get endDate() {
    return Number(this.props.navigation.getParam("endDate"));
  }
  @computed get timesheetId() {
    return Number(this.props.navigation.getParam("timesheetId"));
  }
  @computed get reportTitle() {
    return this.timesheetId > -1
      ? `Timesheet: ${this.timesheetId}`
      : UIText.reportCompletedShift;
  }
  @computed get approvalAvailable() {
    return (
      !this.isSelf &&
      !isEmpty(this.group) &&
      this.timesheetId !== -1 &&
      this.hasHourlyRate &&
      this.caregiverMember.roleList.some(
        r => r.groupTypeRoleId === groupTypeRoleIds.paidCaregiver
      )
    );
  }
  @computed get disapproveDate() {
    return getDisapprovalTime(this.reportDataTopics);
  }
  @computed get disapprovalAvailable() {
    return !this.approvalGiven && !this.disapproveDate;
  }

  @computed get reportRequestData() {
    return {
      groupId: this.groupId,
      caregiverId: this.caregiverId,
      startDate: this.startDate,
      endDate: this.endDate,
      timesheetId: this.timesheetId
    };
  }

  @computed get reportDataTopics() {
    return this.reportData.detail || [];
  }

  constructor(props) {
    this.props = props;
    this.lastScreen = stateController.currentScreen;
    stateController.currentScreen = "ShiftSummary";

    this._initialize();
  }

  componentWillUnmount = () => {
    this.disposer && this.disposer();
  };

  _showError = err => {
    console.warn(err);
    stateController.showPopup({
      title: this.reportTitle,
      content:
        (err.response && JSON.stringify(err.response.data).replace(/"/g, "")) ||
        err.message,
      leftButtonText: UIText.generalConfirm,
      dismissOnBackPress: true
    });
  };

  _initialize = async () => {
    const validParams =
      this.groupId &&
      this.caregiverId &&
      ((this.startDate && this.endDate) || this.timesheetId);
    if (!validParams) return this.handleBackPress();

    this.loading = true;

    return this._getGroups()
      .then(this._getReportData)
      .then(this._calculateApprovalState)
      .then(this._getHtmlContent)
      .catch(this._showError)
      .finally(() => (this.loading = false));
  };

  _getGroups = async () => {
    if (!stateController.viewGroupId && clientController.loginState)
      stateController.viewGroupId = Number(
        this.props.navigation.getParam("group")
      );

    if (
      (this.isVisitor ||
        isEmpty(this.group) ||
        !serviceRecipientGroupTypeIds.includes(this.group.typeId) ||
        paidCaregiverFeatureRestricted(this.group, this.selfMember)) &&
      !this.isAdmin
    ) {
      stateController.viewGroupId = 0;
      return this.handleBackPress();
    }

    return Promise.resolve();
  };

  _getReportData = async () => {
    return apiController
      .getReportCompletedShiftSummary(this.reportRequestData)
      .then(reportData => {
        const disapprovalTime = getDisapprovalTime(reportData.detail);

        if (disapprovalTime > 0) {
          this.pendingDisapprovalTime = 0;
        } else if (this.pendingDisapprovalTime > 0) {
          reportData = {
            ...reportData,
            detail: applyDisapprovalTime(
              reportData.detail,
              this.pendingDisapprovalTime
            )
          };
        }

        this.reportData = reportData;
        return reportData;
      });
  };

  _applyLocalDisapprovalState = disapprovalTime => {
    if (!disapprovalTime) return;

    this.reportData = {
      ...this.reportData,
      detail: applyDisapprovalTime(this.reportDataTopics, disapprovalTime)
    };
  };

  _calculateApprovalState = async data => {
    let shifts = (Array.isArray(data.detail) && data.detail) || [];

    shifts = shifts.map(shift => {
      shift["isApproved"] = evalStringBoolean(shift["isApproved"]);
      return shift;
    });

    this.approvalGiven =
      // !shifts.some(shift => shift["isApproved"] === "unset") && // There should not be any "unset".
      // !shifts.some(
      //   shift =>
      //     (!!shift["isApproved"] && shifts.some(s => !s["isApproved"])) || // There should not be any approved and disapproved at the same time.
      //     (!shift["isApproved"] && shifts.some(s => !!s["isApproved"])) // There should not be any disapproved and approved at the same time.
      // ) &&
      // !shifts.some(shift => !shift["isApproved"]); // If not approved then still light up the buttons.
      shifts.filter(
        shift =>
          typeof shift["isApproved"] === "boolean" && !!shift["isApproved"]
      ).length === shifts.length;

    this.hasHourlyRate = shifts.some(shift => !!shift.hourlyRate);

    return data;
  };

  _getHtmlContent = async data => {
    // data.timezone = defaultLocalizedTimezoneName;
    return apiController
      .getParsedHtmlTemplate("shiftSummary", data)
      .then(html => (this.reportHtml = html))
      .then(() => (this.buttonReady = true));
  };

  handleBackPress = event => {
    return this.props.navigation.navigate(this.lastScreen || "Group");
  };

  handleApprovalPress = event => {
    const data = {
      ...this.reportRequestData,
      isApproved: true
    };

    this.approveLoading = true;
    return this.submitApproval(data)
      .then(() =>
        this.reportDataTopics
          .map(t => {
            t.id = t.topicId;
            return t;
          })
          .map(lockTopic)
      )
      .catch(this._showError)
      .finally(() => (this.approveLoading = false));
  };

  promptForDisapprovalReason = () =>
    new Promise(resolve => {
      const data = observable({
        disapprovalReason: ""
      });

      const closePopup = value =>
        stateController.dismissPopup().then(() => resolve(value || ""));

      const updateSubmitState = () => {
        stateController.popup.rightButtonDisabled = !canSubmitDisapprovalReason(
          data.disapprovalReason
        );
      };

      stateController.showPopup({
        title: this.reportTitle,
        leftButtonText: UIText.generalCancel,
        leftButtonPress: () => closePopup(""),
        rightButtonText: UIText.generalNext,
        rightButtonDisabled: true,
        rightButtonPress: () => {
          const normalizedReason = normalizeDisapprovalReason(
            data.disapprovalReason
          );
          if (!normalizedReason) return Promise.resolve();
          return closePopup(normalizedReason);
        },
        input: {
          heading: UIText.reportCompletedShiftDisapprvReasonPrompt,
          title: UIText.reportCompletedShiftDisapprvReasonTitle,
          placeholder: UIText.reportCompletedShiftDisapprvReasonPlaceholder,
          value: data.disapprovalReason,
          multiline: true,
          autoLineHeight: true,
          heightOverride: 120,
          maxLength: 1000,
          onChange: event => {
            if (!event) return;
            if (
              event.nativeEvent &&
              typeof event.nativeEvent.text === "string"
            ) {
              data.disapprovalReason = event.nativeEvent.text;
            }
            updateSubmitState();
          }
        },
        onBackdropPress: () => closePopup("")
      });
    });

  handleDisapprovalPress = async event => {
    const disapprovalReason = await this.promptForDisapprovalReason();
    if (!canSubmitDisapprovalReason(disapprovalReason)) return;

    const data = {
      ...this.reportRequestData,
      isApproved: false,
      disapprovalReason: normalizeDisapprovalReason(disapprovalReason)
    };

    this.disapproveLoading = true;
    return this.submitApproval(data)
      .catch(this._showError)
      .finally(() => (this.disapproveLoading = false));
  };

  submitApproval = data => {
    this.buttonReady = false;

    const { isApproved } = data;

    const execApproval = async () =>
      stateController
        .showPopup({
          title: this.reportTitle,
          content: UIText.pleaseWait
        })
        .then(() =>
          apiService.async("POST", {
            endpoint: mcbEndpointConfig.shift_summary_approval_disapproval,
            data
          })
        )
        .then(response => {
          if (isApproved) {
            this.pendingDisapprovalTime = 0;
            return response;
          }

          const disapprovalTime = new Date().getTime();
          this.pendingDisapprovalTime = disapprovalTime;
          this._applyLocalDisapprovalState(disapprovalTime);

          return response;
        })
        .then(this._initialize)
        .then(stateController.dismissPopup);

    return new Promise((resolve, reject) => {
      stateController.showPopup({
        title: this.reportTitle,
        content: UIText.reportCompletedShiftConfirm(isApproved),
        leftButtonText: UIText.generalNo,
        leftButtonPress: () => stateController.dismissPopup().then(resolve),
        rightButtonText: UIText.generalYes,
        rightButtonPress: () =>
          stateController
            .dismissPopup()
            .then(execApproval)
            .then(resolve)
            .catch(reject),
        onBackdropPress: () => stateController.dismissPopup().then(resolve)
      });
    }).finally(() => (this.buttonReady = true));
  };

  onRefresh = () => {
    return this._initialize();
  };

  onScroll = event => {};
}
